import { delegate } from '@atomify/core';
import { defineElement, FC, onDidLoad, useElement, useListen } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { elements } from '@utilities/dom';

const CLASS_IS_OPEN = 'chat--is-open';
const CLASS_IS_ACTIVE = 'chat--is-active';

const CLASS_STATUS_OFFLINE = 'chat--status-offline';
const CLASS_STATUS_AWAY = 'chat--status-away';
const CLASS_STATUS_ONLINE = 'chat--status-online';
const CLASS_STATUS_UNREAD = 'chat--status-unread';

const STATUS_CLASSES = [
    CLASS_STATUS_OFFLINE,
    CLASS_STATUS_AWAY,
    CLASS_STATUS_ONLINE,
    CLASS_STATUS_UNREAD,
];

interface MessageInterface {
    active: boolean;
    pane?: string;
}

const Chat: FC = ({ element }) => {
    const closeButton = useElement<HTMLElement>('[js-hook-chat-close]', { root: document });

    const iframe: HTMLIFrameElement = document.createElement('iframe');
    let setPaneOnLoad: string | undefined = undefined;

    function blackboxUrl() {
        return element.dataset.blackboxUrl as string;
    }

    function isActive() {
        return element.dataset.active === 'true';
    }

    useListen(window, 'message', (e: any) => {
        const { status, unread: unreadMessages } = e.data;

        if (setPaneOnLoad) {
            open(setPaneOnLoad);
            setPaneOnLoad = undefined;
        }

        if (status) setStatus(status, unreadMessages);
    });

    useListen(closeButton, 'click', (e: Event) => {
        e.stopPropagation();

        close();
    });

    onDidLoad(() => {
        appendIframe();
        setupDelegate();

        if (isActive()) {
            elements.body.classList.add(CLASS_IS_ACTIVE);
        }

        if (element.dataset.open === 'true') {
            setPaneOnLoad = 'message';
        }
    });

    function open(pane?: string) {
        sendMessage({
            active: true,
            pane,
        });

        elements.body.classList.add(CLASS_IS_OPEN);
    }

    function close() {
        sendMessage({
            active: false,
        });

        elements.body.classList.remove(CLASS_IS_OPEN);
    }

    function renderCloseButton() {
        return (
            <button class="chat__close-button" onClick={() => close()}>
                <bpd-svg name="icons-close" />
            </button>
        );
    }

    function setupDelegate() {
        delegate({
            selector: '[js-hook-chat-open]',
            target: document,
            type: 'click',
            callback: (event) => {
                const button = (event as any).delegateTarget as HTMLButtonElement;
                const pane = button.dataset.pane;

                open(isActive() ? pane : 'message');
            },
        });
    }

    function setStatus(status: string, unreadMessages: number) {
        let nextStatusClass: string | boolean = false;

        switch (status) {
            case 'offline':
                nextStatusClass = CLASS_STATUS_OFFLINE;
                break;
            case 'away':
                nextStatusClass = CLASS_STATUS_AWAY;
                break;
            case 'online':
                nextStatusClass = unreadMessages > 0 ? CLASS_STATUS_UNREAD : CLASS_STATUS_ONLINE;
                break;
        }

        if (!nextStatusClass) return;

        // Only swap status class if it's different
        if (!elements.body.classList.contains(nextStatusClass)) {
            elements.body.classList.remove(...STATUS_CLASSES);
            elements.body.classList.add(nextStatusClass);
        }
    }

    function appendIframe() {
        // Rendering iframes in webcomponent doesn't work.
        // Append iframe in body instead
        const src = constructSrc();
        const wrapper = document.createElement('div');

        iframe.src = src;
        iframe.title = 'blackbox';

        wrapper.classList.add('chat__frame');
        wrapper.appendChild(renderCloseButton());
        wrapper.appendChild(iframe);

        elements.body.append(wrapper);
    }

    function sendMessage({ active, pane }: MessageInterface) {
        const targetWindow = iframe.contentWindow as Window;

        targetWindow.postMessage(
            {
                active: active ? 'true' : 'false',
                pane,
                agent: {
                    name: element.dataset.agentName,
                    picture: element.dataset.agentPicture,
                },
                user: {
                    firstname: element.dataset.userFirstname,
                    lastname: element.dataset.userLastname,
                    emailaddress: element.dataset.userEmailaddress,
                    phonenumber: element.dataset.userPhonenumber,
                },
                contact: {
                    phonenumber: element.dataset.contactPhonenumber,
                    weekdays: element.dataset.contactHoursWeekdays,
                    saturday: element.dataset.contactHoursSaturday,
                    sunday: element.dataset.contactHoursSunday,
                },
                areaid: element.dataset.areaId,
                districtid: element.dataset.districtId,
            },
            blackboxUrl(),
        );
    }

    function constructSrc() {
        const encode = (strings: TemplateStringsArray, ...parts: string[]) => {
            const src = parts.reduce((src, part, i) => {
                return `${src}${strings[i] ? strings[i].trim() : ''}${encodeURI(part)}`;
            }, '');

            return src.replace(/\s/g, '');
        };

        let src = blackboxUrl();

        src += encode`/blackbox/index.html
            ?origin=${window.location.origin}
            &cookie-accepted=${element.dataset.cookieAccepted ? 'true' : 'false'}
            &location=${blackboxUrl() as string}
            &client=${element.dataset.client as string}
            &client-key=${element.dataset.clientKey as string}
            &area-id=${element.dataset.areaId as string}
            &page-title=${element.dataset.pageTitle as string}
            &page-url=${element.dataset.pageUrl as string}
            &page-referer=${element.dataset.pageReferer as string}
            &page-domain=${element.dataset.pageDomain as string}
            &contact-phone-number=${element.dataset.contactPhonenumber as string}
            &contact-hours-weekdays=${element.dataset.contactHoursWeekdays as string}
            &contact-hours-saturday=${element.dataset.contactHoursSaturday as string}
            &contact-hours-sunday=${element.dataset.contactHoursSunday as string}
            &timeout-switch=${element.dataset.timeoutSwitch as string}
            &timeout-duration=${element.dataset.timeoutDuration as string}
            &timeout-message=${element.dataset.timeoutMessage as string}
        `;

        return src;
    }
};

defineElement('bpd-chat', Chat);
